import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { XMarkIcon, BriefcaseIcon, ChevronDownIcon, CheckIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button } from "components/ui";
import { getAccountantCounsellors, assignAccountantCounsellor } from "services/leadsService";
import { toast } from "sonner";
import clsx from "clsx";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { useAuthContext } from "app/contexts/auth/context";

const AssignAccountantCounsellorModal = ({
  isOpen,
  onClose,
  leadData,
  onAccountantCounsellorChange,
  currentAccountantCounsellorName,
}) => {
  const { user: loggedInUser } = useAuthContext();
  const [selectedCounsellor, setSelectedCounsellor] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [counsellors, setCounsellors] = useState([]);
  const [isLoadingCounsellors, setIsLoadingCounsellors] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const buttonRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0, width: 0 });
  const [showReassignConfirm, setShowReassignConfirm] = useState(false);

  const positionMenu = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPosition({ left: rect.left, top: rect.bottom + 6, width: rect.width });
  };

  useEffect(() => {
    const handleRecalc = () => positionMenu();
    window.addEventListener("resize", handleRecalc, { passive: true });
    window.addEventListener("scroll", handleRecalc, { passive: true, capture: true });
    return () => {
      window.removeEventListener("resize", handleRecalc);
      window.removeEventListener("scroll", handleRecalc, { capture: true });
    };
  }, []);

  const saveRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchCounsellors();
      setSelectedCounsellor("");
      setSearchQuery("");
    }
  }, [isOpen]);

  const fetchCounsellors = async () => {
    setIsLoadingCounsellors(true);
    try {
      const response = await getAccountantCounsellors();
      if (response.success) {
        setCounsellors(response.data || []);
      } else {
        toast.error("Failed to load accountant counsellors", {
          description: response.error || "Unable to fetch counsellors",
        });
        setCounsellors([]);
      }
    } catch (error) {
      console.error("Error fetching accountant counsellors:", error);
      toast.error("Failed to load accountant counsellors");
      setCounsellors([]);
    } finally {
      setIsLoadingCounsellors(false);
    }
  };

  const filteredCounsellors = useMemo(() => {
    if (!searchQuery) return counsellors;
    const lowerQuery = searchQuery.toLowerCase();
    return counsellors.filter((c) =>
      (c.FullName || c.fullName || "").toLowerCase().includes(lowerQuery)
    );
  }, [counsellors, searchQuery]);

  const handleSubmit = async () => {
    if (!selectedCounsellor) {
      toast.error("Please select an accountant counsellor");
      return;
    }
    if (
      currentAccountantCounsellorName &&
      currentAccountantCounsellorName !== "Not Assigned" &&
      currentAccountantCounsellorName !== "N/A"
    ) {
      setShowReassignConfirm(true);
      return;
    }
    await performAssignment();
  };

  const performAssignment = async () => {
    setIsSubmitting(true);
    setShowReassignConfirm(false);
    try {
      const payload = {
        leadId: leadData?.id,
        accountantCounsellorId: selectedCounsellor,
        companyId: loggedInUser?.companyId,
        modifiedBy: loggedInUser?.userId,
      };

      const result = await assignAccountantCounsellor(payload);

      if (result.success) {
        toast.success(result.message || "Accountant counsellor assigned successfully");
        if (onAccountantCounsellorChange) {
          await onAccountantCounsellorChange();
        }
        onClose();
      } else {
        toast.error(result.error || "Failed to assign accountant counsellor");
      }
    } catch (error) {
      console.error("Error assigning accountant counsellor:", error);
      toast.error("Failed to assign accountant counsellor");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => {}}
        initialFocus={saveRef}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-lg origin-top flex-col rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none">
            {/* Header */}
            <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 dark:bg-dark-800 sm:px-5">
              <DialogTitle
                as="h3"
                className="flex items-center gap-2 text-base font-medium text-gray-800 dark:text-dark-100"
              >
                <BriefcaseIcon className="h-5 w-5 text-amber-600" />
                Assign Accountant Counsellor
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex flex-col overflow-y-auto px-4 py-4 sm:px-5 max-h-[calc(85vh-140px)]">
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Manually assign an accountant counsellor for{" "}
                {leadData?.name || "this lead"}. This will override any automatic assignment.
              </p>

              {currentAccountantCounsellorName &&
                currentAccountantCounsellorName !== "Not Assigned" &&
                currentAccountantCounsellorName !== "N/A" && (
                  <div className="mb-4 rounded-lg bg-amber-50 px-4 py-3 dark:bg-amber-900/20">
                    <div className="flex items-center gap-2">
                      <BriefcaseIcon className="size-5 text-amber-600 dark:text-amber-400" />
                      <div>
                        <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                          Currently Assigned
                        </p>
                        <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                          {currentAccountantCounsellorName}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              <div className="space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select Accountant Counsellor *
                  </label>

                  {isLoadingCounsellors ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="size-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" />
                      <span className="ml-2 text-sm text-gray-500">Loading counsellors...</span>
                    </div>
                  ) : (
                    <Menu as="div" className="relative w-full">
                      {({ open }) => (
                        <>
                          <MenuButton
                            as={Button}
                            variant="outlined"
                            className="w-full justify-between"
                            disabled={isSubmitting}
                            ref={buttonRef}
                            onClick={positionMenu}
                          >
                            <span className="truncate">
                              {selectedCounsellor
                                ? (
                                    counsellors.find(
                                      (c) =>
                                        (c.UserId || c.userId) === selectedCounsellor
                                    )?.FullName ||
                                    counsellors.find(
                                      (c) =>
                                        (c.UserId || c.userId) === selectedCounsellor
                                    )?.fullName ||
                                    "Select Counsellor"
                                  )
                                : "Select Counsellor"}
                            </span>
                            <ChevronDownIcon
                              className={clsx(
                                "size-4 transition-transform",
                                open && "rotate-180"
                              )}
                              aria-hidden="true"
                            />
                          </MenuButton>

                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="opacity-0 translate-y-1"
                            enterTo="opacity-100 translate-y-0"
                            leave="transition ease-in duration-75"
                            leaveFrom="opacity-100 translate-y-0"
                            leaveTo="opacity-0 translate-y-1"
                          >
                            <MenuItems
                              portal
                              static
                              className="fixed z-[9999] max-h-[60vh] overflow-hidden rounded-lg border border-gray-300 bg-white shadow-lg shadow-gray-200/50 outline-none focus-visible:outline-none dark:border-dark-500 dark:bg-dark-700 dark:shadow-none"
                              style={{
                                left: `${menuPosition.left}px`,
                                top: `${menuPosition.top}px`,
                                width: `${menuPosition.width}px`,
                              }}
                            >
                              <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-3 py-2 dark:border-dark-500 dark:bg-dark-700">
                                <input
                                  type="text"
                                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-dark-500 dark:bg-dark-600 dark:text-dark-100 dark:placeholder-gray-400"
                                  placeholder="Search counsellors..."
                                  value={searchQuery}
                                  onChange={(e) => setSearchQuery(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>

                              <div className="max-h-[150px] overflow-y-auto">
                                {filteredCounsellors.length === 0 ? (
                                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                    {searchQuery ? "No counsellors found" : "No counsellors available"}
                                  </div>
                                ) : (
                                  filteredCounsellors.map((c) => {
                                    const id = c.UserId || c.userId;
                                    const name = c.FullName || c.fullName;
                                    return (
                                      <MenuItem key={id}>
                                        {({ focus }) => (
                                          <button
                                            onClick={() => setSelectedCounsellor(id)}
                                            className={clsx(
                                              "flex h-9 w-full items-center px-3 tracking-wide outline-none transition-colors",
                                              focus &&
                                                "bg-gray-100 text-gray-800 dark:bg-dark-600 dark:text-dark-100",
                                              selectedCounsellor === id &&
                                                "bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300"
                                            )}
                                          >
                                            <span className="flex-1 truncate text-left text-sm">
                                              {name}
                                            </span>
                                            {selectedCounsellor === id && (
                                              <CheckIcon className="size-4 text-primary-600 dark:text-primary-400" />
                                            )}
                                          </button>
                                        )}
                                      </MenuItem>
                                    );
                                  })
                                )}
                              </div>
                            </MenuItems>
                          </Transition>
                        </>
                      )}
                    </Menu>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-4 py-4 dark:border-dark-600 sm:px-5">
              <div className="flex justify-end space-x-3">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  color="primary"
                  ref={saveRef}
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting || !selectedCounsellor}
                >
                  {isSubmitting ? "Assigning..." : "Assign Counsellor"}
                </Button>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>

      <ConfirmModal
        show={showReassignConfirm}
        state="pending"
        onClose={() => setShowReassignConfirm(false)}
        onOk={performAssignment}
        confirmLoading={isSubmitting}
        messages={{
          pending: {
            title: "Reassign Accountant Counsellor?",
            description: `This lead is already assigned to ${currentAccountantCounsellorName}. Do you want to reassign?`,
            actionText: "Yes, Reassign",
          },
        }}
        buttonColor="primary"
      />
    </Transition>
  );
};

export default AssignAccountantCounsellorModal;
